import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRepairContext } from "@/context/RepairContext";

import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { format } from "date-fns";
import {
  generateQRCodeDataURL,
  generatePrintQRCodeDataURL,
} from "@/utils/qrCodeUtils";
import { generateRepairBarcode } from "@/utils/barcodeUtils";
import { formatTicketNumber } from "@/utils/ticketNumberUtils";
// Removed expensive branch name utilities for performance optimization
// Direct SQL approach removed
import { toast } from "sonner";
import {
  ArrowLeft,
  Printer,
  Download,
  AlertTriangle,
  CheckCircle2,
  Phone,
  Smartphone,
  Calendar,
  Receipt,
  FileText,
  Ticket,
  Plus,
  X,
  ClipboardList,
  QrCode,
  Copy,
  PhoneCall,
  User,
  Clock,
  Wrench,
  Store,
} from "lucide-react";
import {
  RepairItem,
  Observation,
  PriceModification,
  StatusHistory,
} from "@/types";
import RepairTimeline from "@/components/RepairTimeline";
import { logoConfig, logoPrintConfig } from "@/config/appConfig";

const RepairDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { getRepairById, updateRepair, deleteRepair } = useRepairContext();
  const navigate = useNavigate();
  const [repair, setRepair] = useState<RepairItem | undefined>(undefined);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [barcode, setBarcode] = useState<string | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [deleteCode, setDeleteCode] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [loading, setLoading] = useState(true);
  const [codeType, setCodeType] = useState<"qr" | "barcode">("qr"); // Default to QR code
  const [newObservation, setNewObservation] = useState("");
  const [newPriceModification, setNewPriceModification] = useState({
    amount: 0,
    reason: "",
  });
  // Payment confirmation state
  const [paymentConfirmDialogOpen, setPaymentConfirmDialogOpen] =
    useState(false);
  const [paymentConfirmCode, setPaymentConfirmCode] = useState("");
  const [paymentConfirmError, setPaymentConfirmError] = useState("");
  const { t, i18n } = useTranslation();

  // Set up keyboard shortcuts
  useKeyboardShortcuts({
    onPrint: () => {
      if (repair) {
        handleOpenPrintDialog();
      }
    },
  });

  useEffect(() => {
    const fetchRepair = async () => {
      if (id) {
        setLoading(true);
        try {
          const foundRepair = await getRepairById(id);
          setRepair(foundRepair);

          if (foundRepair) {
            generateQRCode(foundRepair.id);
            generateBarcode(false); // Generate regular barcode for standalone printing

            // Removed debug call for performance optimization
          }
        } catch (error) {
          console.error("Error fetching repair:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchRepair();

    // Set up real-time subscription for this specific repair
    if (id) {
      const repairSubscription = supabase
        .channel(`repair-${id}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "repairs",
            filter: `id=eq.${id}`,
          },
          async (payload) => {
            console.log("Repair updated:", payload);
            // Fetch the updated repair directly from the database
            const updatedRepair = await getRepairById(id);
            if (updatedRepair) {
              setRepair(updatedRepair);
            }
          }
        )
        .subscribe();

      // Clean up subscription when component unmounts
      return () => {
        supabase.removeChannel(repairSubscription);
      };
    }
  }, [id, getRepairById]);

  const generateQRCode = async (repairId: string) => {
    try {
      const qrDataUrl = await generateQRCodeDataURL(repairId);
      setQrCode(qrDataUrl);

      // Branch name is now stored in the repair record, no need for expensive RPC call
    } catch (error) {
      console.error("Failed to generate QR code:", error);
    }
  };

  const generateBarcode = async (isForTicket: boolean = false) => {
    try {
      if (!repair) return;

      // Always use the ticket number for the barcode
      const ticketNumber = repair.ticketNumber?.toString() || "";

      if (ticketNumber) {
        console.log("Generating barcode for ticket number:", ticketNumber);

        try {
          // Generate a barcode using ONLY the ticket number
          const barcodeDataUrl = await generateRepairBarcode(
            ticketNumber, // Use ticket number as the content
            isForTicket
          );
          setBarcode(barcodeDataUrl);
        } catch (barcodeError) {
          console.error("Error generating barcode:", barcodeError);
          toast.error(t("repair.scanError"));
        }
      } else {
        // If no ticket number is available, we can't generate a proper barcode
        console.error("No ticket number available for barcode generation");
        toast.error(t("repair.noTicketNumberForBarcode"));
      }
    } catch (error) {
      console.error("Failed to generate barcode:", error);
      toast.error(t("repair.scanError"));
    }
  };

  const handleStatusChange = async (value: string) => {
    if (repair && id) {
      const statusUpdate = value as
        | "pending"
        | "inProgress"
        | "completed"
        | "cancelled"
        | "returned";

      // Don't update if status hasn't changed
      if (statusUpdate === repair.status) {
        return;
      }

      const updates: Partial<RepairItem> = {
        status: statusUpdate,
      };

      if (statusUpdate === "completed" && repair.status !== "completed") {
        updates.completedAt = new Date();
      }

      // If status is changed to "returned" from "completed", keep the completedAt date
      if (
        statusUpdate === "returned" &&
        repair.status !== "returned" &&
        !repair.completedAt
      ) {
        updates.completedAt = new Date();
      }

      try {
        // Only add a status history entry if the status is actually changing
        // and we're not in the initial creation (where status history is already initialized)
        if (repair.statusHistory && repair.statusHistory.length > 0) {
          // Check if the last status entry is different from the new one
          const lastStatusEntry =
            repair.statusHistory[repair.statusHistory.length - 1];

          // Only add a new entry if the status is different
          if (lastStatusEntry.status !== statusUpdate) {
            // Create a new status history entry
            const newStatusHistory: StatusHistory = {
              id: crypto.randomUUID(),
              status: statusUpdate,
              createdAt: new Date(),
              userId: repair.userId,
            };

            // Add to existing history
            const updatedStatusHistory = [
              ...repair.statusHistory,
              newStatusHistory,
            ];
            updates.statusHistory = updatedStatusHistory;
          }
        } else {
          // If no status history exists yet, initialize it
          const newStatusHistory: StatusHistory = {
            id: crypto.randomUUID(),
            status: statusUpdate,
            createdAt: new Date(),
            userId: repair.userId,
          };

          updates.statusHistory = [newStatusHistory];
        }
      } catch (error) {
        console.log("Status history might not be available yet, skipping");
        // Continue without status history
      }

      await updateRepair(id, updates);
      setRepair({ ...repair, ...updates });
      toast.success(t("repair.successUpdated"));
    }
  };

  const handlePaymentStatusChange = async (value: string) => {
    if (repair && id) {
      const paymentStatusUpdate = value as "paid" | "partial" | "unpaid";

      // Create the update object
      const updates: Partial<RepairItem> = {
        paymentStatus: paymentStatusUpdate,
      };

      // If status is changing to paid, set the paidAt date
      if (paymentStatusUpdate === "paid" && repair.paymentStatus !== "paid") {
        updates.paidAt = new Date();
      } else if (paymentStatusUpdate !== "paid") {
        // If changing from paid to another status, clear the paidAt date and payment confirmation
        updates.paidAt = undefined;
        updates.paymentConfirmed = false;
        updates.paymentConfirmedAt = undefined;
      }

      await updateRepair(id, updates);
      setRepair({ ...repair, ...updates });
      toast.success(t("repair.successUpdated"));
    }
  };

  const handleAddObservation = async () => {
    if (repair && id && newObservation.trim()) {
      try {
        console.log("Adding observation:", newObservation.trim());

        // Create a new observation object for local state update
        const newObservationObj = {
          id: crypto.randomUUID(),
          text: newObservation.trim(),
          createdAt: new Date(),
          userId: repair.userId,
        };

        console.log("New observation object:", newObservationObj);

        // Send only the new observation text as a string array
        // The updateRepair function will convert it to an Observation object
        await updateRepair(id, {
          observations: [newObservation.trim()] as unknown as Observation[],
        });

        // Clear the input immediately
        setNewObservation("");

        // Fetch the latest repair data to ensure we have the most up-to-date observations
        console.log("Fetching updated repair data after adding observation");
        const updatedRepair = await getRepairById(id);
        if (updatedRepair) {
          console.log("Updated repair received:", updatedRepair);
          console.log(
            "Observations in updated repair:",
            updatedRepair.observations
          );
          setRepair(updatedRepair);
        } else {
          // Fallback to local update if fetch fails
          console.log("Failed to fetch updated repair, using local update");
          setRepair({
            ...repair,
            observations: [...(repair.observations || []), newObservationObj],
          });
        }

        toast.success(t("repair.observationAdded"));
      } catch (error) {
        console.error("Error adding observation:", error);
        toast.error(t("repair.observationAddError"));
      }
    }
  };

  const handleAddPriceModification = async () => {
    if (
      repair &&
      id &&
      newPriceModification.reason.trim() &&
      newPriceModification.amount !== 0
    ) {
      try {
        console.log("Adding price modification:", newPriceModification);

        // Create a new price modification object for local state update
        const newPriceModificationObj = {
          id: crypto.randomUUID(),
          amount: newPriceModification.amount,
          reason: newPriceModification.reason.trim(),
          createdAt: new Date(),
          userId: repair.userId,
        };

        console.log("New price modification object:", newPriceModificationObj);

        // Send the new price modification object
        await updateRepair(id, {
          priceModifications: [newPriceModificationObj] as PriceModification[],
        });

        // Clear the input immediately
        setNewPriceModification({ amount: 0, reason: "" });

        // Fetch the latest repair data to ensure we have the most up-to-date price modifications
        console.log(
          "Fetching updated repair data after adding price modification"
        );
        const updatedRepair = await getRepairById(id);
        if (updatedRepair) {
          console.log("Updated repair received:", updatedRepair);
          console.log(
            "Price modifications in updated repair:",
            updatedRepair.priceModifications
          );
          setRepair(updatedRepair);
        } else {
          // Fallback to local update if fetch fails
          console.log("Failed to fetch updated repair, using local update");
          setRepair({
            ...repair,
            priceModifications: [
              ...(repair.priceModifications || []),
              newPriceModificationObj,
            ],
          });
        }

        toast.success(t("repair.priceModificationAdded"));
      } catch (error) {
        console.error("Error adding price modification:", error);
        toast.error(t("repair.priceModificationAddError"));
      }
    }
  };

  const handleDelete = async () => {
    // Get the deletion code from environment variables
    const correctCode = "5099";

    // Reset error state
    setDeleteError("");

    // Check if the entered code matches the correct code
    if (deleteCode !== correctCode) {
      setDeleteError(t("repair.invalidDeleteCode"));
      return;
    }

    if (id) {
      await deleteRepair(id);
      toast.success(t("repair.successDeleted"));
      setDeleteDialogOpen(false);
      setDeleteCode(""); // Reset the code input
      navigate("/");
    }
  };

  // Payment confirmation handler
  const handleConfirmPayment = async () => {
    // Get the payment confirmation code from environment variables
    // Using the same code as delete for simplicity, but you can use a different one
    const correctCode = "5099";

    // Reset error state
    setPaymentConfirmError("");

    // Check if the entered code matches the correct code
    if (paymentConfirmCode !== correctCode) {
      setPaymentConfirmError(t("repair.invalidConfirmCode"));
      return;
    }

    if (repair && id) {
      try {
        const updates: Partial<RepairItem> = {
          paymentConfirmed: true,
          paymentConfirmedAt: new Date(),
        };

        await updateRepair(id, updates);
        setRepair({ ...repair, ...updates });
        toast.success(t("repair.paymentConfirmed"));
        setPaymentConfirmDialogOpen(false);
        setPaymentConfirmCode(""); // Reset the code input
        setPaymentConfirmError(""); // Reset error state
      } catch (error) {
        console.error("Error confirming payment:", error);
        toast.error(t("repair.updateError"));
      }
    }
  };

  const handlePrintQR = async () => {
    if (!repair?.id) return;

    try {
      // Generate high-resolution QR code specifically for printing
      const printQrCode = await generatePrintQRCodeDataURL(repair.id);

      // Open a window that will adapt to the printer's capabilities
      const printWindow = window.open(
        "",
        "_blank",
        "width=400,height=400,menubar=no,toolbar=no,location=no,scrollbars=yes,status=no,resizable=yes"
      );
      if (printWindow) {
        printWindow.document.write(`
        <html>
          <head>
            <title>${t("repair.scanQrCode")} - ${repair?.customerName}</title>
            <style>
              @page {
                margin: 0;
                size: auto;
              }
              html, body {
                height: auto;
                overflow: visible;
              }
              body {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 0;
                margin: 0;
                font-family: Arial, sans-serif;
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
              }
              .qr-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                border: 1px solid #000;
                padding: 10px;
                margin-top: 0;
                width: calc(100% - 20px);
                box-sizing: border-box;
              }
              img {
                width: 300px;
                height: 300px;
                image-rendering: -webkit-optimize-contrast;
                image-rendering: crisp-edges;
                image-rendering: pixelated;
              }
              .details {
                margin-top: 10px;
                text-align: center;
              }
              h3 {
                margin: 5px 0;
              }
              p {
                margin: 2px 0;
                font-size: 14px;
              }
            </style>
          </head>
          <body>
            <div class="qr-container">
              <img src="${printQrCode}" alt="QR Code" />
              <div class="details">
                <h3>${repair?.customerName}</h3>
                <p>${repair?.phoneModel}</p>
                <p>${format(
                  new Date(repair?.createdAt || new Date()),
                  "dd/MM/yyyy - HH:mm"
                )}</p>
              </div>
            </div>
            <script>
              // Print immediately without waiting for full load
              document.addEventListener('DOMContentLoaded', function() {
                // Small timeout to ensure the browser has rendered the content
                setTimeout(function() {
                  window.print();
                  window.onafterprint = function() {
                    window.close();
                  };
                }, 100);
              });

              // Fallback in case DOMContentLoaded doesn't trigger
              window.onload = function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                };
              };
            </script>
          </body>
        </html>
        `);
        printWindow.document.close();
      }
    } catch (error) {
      console.error("Failed to generate print QR code:", error);
      toast.error(t("repair.scanError"));
    }
  };

  const handlePrintBarcode = async () => {
    if (!repair) return;

    try {
      // Always use the ticket number for the barcode
      const ticketNumber = repair.ticketNumber?.toString() || "";

      if (!ticketNumber) {
        toast.error(t("repair.noTicketNumberForBarcode"));
        return;
      }

      // Generate barcode using the same approach as QR code
      const printBarcode = await generateRepairBarcode(ticketNumber, false);

      // Open a window that will adapt to the printer's capabilities (same as QR code)
      const printWindow = window.open(
        "",
        "_blank",
        "width=800,height=600,menubar=no,toolbar=no,location=no,scrollbars=yes,status=no,resizable=yes"
      );
      if (printWindow) {
        printWindow.document.write(`
        <html>
          <head>
            <title>${t("repair.scanBarcode")} - ${repair?.customerName}</title>
            <style>
              @page {
                margin: 0;
                size: auto;
              }
              html, body {
                height: auto;
                overflow: visible;
              }
              body {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 0 0 0 0;
                margin: 0;
                font-family: Arial, sans-serif;
                position: absolute;
                top: 0;
                left: 120px;
                width: 35%;
              }
              .barcode-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                border: 0px solid #000;
                padding: 0px;
                margin-top: 0;
                width: calc(100% - 20px);

              }
              img {
                width: 95%;
                max-width: 350px;
                height: 10mm;
                margin-top: 10px;
              }
              .details {
                margin-top: 10px;
                text-align: center;
              }
              h3 {
                margin: 5px 0;
                font-size: 10px;
                font-weight: bold;
              }
              p {
                margin: 2px 0;
                font-size: 14px;
              }
            </style>
          </head>
          <body>
            <div class="barcode-container">
              <img src="${printBarcode}" alt="Barcode" />
              <div class="details">
                <h3>#${repair.ticketNumber}</h3>
              </div>
            </div>
            <script>
              // Print immediately without waiting for full load
              document.addEventListener('DOMContentLoaded', function() {
                // Small timeout to ensure the browser has rendered the content
                setTimeout(function() {
                  window.print();
                  window.onafterprint = function() {
                    window.close();
                  };
                }, 100);
              });

              // Fallback in case DOMContentLoaded doesn't trigger
              window.onload = function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                };
              };
            </script>
          </body>
        </html>
        `);
        printWindow.document.close();
      }
    } catch (error) {
      console.error("Failed to generate print barcode:", error);
      toast.error(t("repair.scanError"));
    }
  };

  const handleDownloadQR = () => {
    if (!qrCode) return;

    const link = document.createElement("a");
    link.href = qrCode;
    link.download = `qr-code-${repair?.id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenPrintDialog = () => {
    if (!repair || !qrCode) return;
    // Automatically set QR code as default
    setCodeType("qr");
    setPrintDialogOpen(true);
  };

  // New function to directly print ticket with QR code (no dialog)
  const handlePrintTicketWithQR = async () => {
    if (!repair || !qrCode) return;
    // Automatically use QR code and print directly
    setCodeType("qr");
    await handlePrintTicket();
  };

  const handlePrintTicket = async () => {
    if (!repair) return;

    // Generate high-resolution QR code for printing if QR is selected
    let printCode = qrCode;
    if (codeType === "qr") {
      printCode = await generatePrintQRCodeDataURL(repair.id);
    }

    // Generate a ticket-specific barcode (smaller) if barcode is selected
    if (codeType === "barcode") {
      await generateBarcode(true);
    }

    // Use the stored ticket number from the database
    const ticketNumber = repair.ticketNumber
      ? repair.ticketNumber.toString()
      : "0"; // Fallback to 0 if no ticket number is available
    const formattedTicketNumber = formatTicketNumber(ticketNumber, "#");

    // Get the absolute URL for the logo
    // This ensures the logo path is correctly resolved in the new window
    const logoUrl = new URL(logoPrintConfig.path, window.location.origin).href;

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat(i18n.language, {
        style: "currency",
        currency: "TND",
      }).format(amount);
    };

    // Open a window that matches 80mm thermal printer width
    const printWindow = window.open(
      "",
      "_blank",
      "width=302,height=600,menubar=no,toolbar=no,location=no,scrollbars=yes,status=no,resizable=yes"
    );
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>${t("repair.repairTicket")} - ${repair.customerName}</title>
            <style>
              @page {
                size: 80mm auto; /* Set width to 80mm for thermal printer */
                margin: 0;
              }
              @media print {
                /* Ensure proper margins and padding for 80mm paper */
                html, body {
                  margin: 0 !important;
                  padding: 0 !important;
                  overflow: hidden;
                  width: 80mm !important;
                  max-width: 80mm !important;
                }
                /* Set ticket width for 80mm paper with padding */
                .ticket {
                  width: 72mm !important; /* 80mm - 8mm (4mm padding on each side) */
                  max-width: 72mm !important;
                  margin: 0 4mm !important; /* 4mm margin on each side */
                  padding: 0 !important;
                  box-sizing: border-box !important;
                }
              }
              html, body {
                height: auto;
                overflow: visible;
                margin: 0;
                padding: 0;
                width: 100%;
                max-width: 100%;
              }
              body {
                font-family: 'Arial', sans-serif; /* Better readability than Courier */
                width: 100%; /* Use full width available */
                max-width: 100%; /* Allow printer to determine max width */
                padding: 0;
                margin: 0; /* No margin */
                font-size: 10px; /* Increased base font size for better readability */
                line-height: 1.4; /* Increased line height for better readability */
                -webkit-print-color-adjust: exact; /* Ensure backgrounds print */
                color-adjust: exact;
                position: relative;
                box-sizing: border-box; /* Include padding in width calculation */
                left: 0; /* Ensure left alignment */
                font-weight: 900; /* Make all text extra bold by default */
              }
              .ticket {
                width: 72mm; /* 80mm - 8mm (4mm padding on each side) */
                max-width: 72mm;
                margin: 0 4mm; /* 4mm margin on each side */
                padding: 0;
                position: relative;
                top: 0;
                left: 0; /* Ensure left alignment */
                box-sizing: border-box; /* Include padding in width calculation */
              }
              .header {
                text-align: center;
                margin-bottom: 5px; /* Reduced margin */
                border-bottom: 1px dashed #000;
                padding-bottom: 3px; /* Reduced padding */
              }
              .logo-container {
                text-align: center;
                margin-bottom: 8px;
                padding-top: 5px;
              }
              .logo-container img {
                max-width: 80px;
                max-height: 80px;
                margin: 0 auto;
                display: block;
                object-fit: contain;
              }
              .shop-name {
                font-size: 16px; /* Increased font size */
                font-weight: 900; /* Extra bold */
                text-decoration: underline; /* Add underline for emphasis */
                margin-bottom: 2px; /* Add some spacing */
              }
              .title {
                font-size: 14px; /* Increased font size */
                font-weight: 900; /* Extra bold */
                margin: 3px 0; /* Reduced margin */
                border-bottom: 2px solid #000; /* Thicker solid underline */
                padding: 2px 0;
                text-decoration: underline; /* Add underline for emphasis */
                text-align: center; /* Center text */
              }
              .section {
                margin-bottom: 5px; /* Reduced margin */
                border-bottom: 1px dashed #000;
                padding-bottom: 3px; /* Reduced padding */
              }
              .info-row {
                display: block; /* Changed from flex to block to prevent right content from being cut */
                margin: 1px 0; /* Further reduced margin */
                width: 100%;
                box-sizing: border-box;
                overflow: hidden; /* Prevent overflow */
              }
              .label {
                font-weight: 900; /* Extra bold */
                text-decoration: underline; /* Underline instead of color for emphasis */
                display: inline-block; /* Changed from flex to inline-block */
                width: 40%; /* Fixed width for labels */
                padding-right: 2px; /* Reduced space between label and value */
                overflow: hidden; /* Prevent overflow */
                text-overflow: ellipsis; /* Add ellipsis for overflow */
                vertical-align: top; /* Align to top */
              }
              .value {
                font-weight: 900; /* Extra bold for values */
                text-align: right;
                display: inline-block; /* Changed from flex to inline-block */
                width: 58%; /* Fixed width for values */
                overflow: hidden; /* Prevent overflow */
                text-overflow: ellipsis; /* Add ellipsis for overflow */
                vertical-align: top; /* Align to top */
                font-size: 16px; /* Slightly larger font */
              }
              .qr-code {
                text-align: center;
                margin-top: 5px; /* Reduced margin */
                width: 100%;
                box-sizing: border-box;
              }
              .qr-code img {
                width: 50px; /* Even smaller QR code */
                height: 50px;
                max-width: 100%;
              }
              .barcode {
                text-align: center;
                margin-top: 5px; /* Reduced margin */
                width: 100%;
                box-sizing: border-box;
                border-top: 1px dashed #000;
                padding-top: 5px;
              }
              .barcode img {
                width: 90%; /* Wider barcode */
                max-width: 90%;
                height: 70px; /* Further increased height for better scanning */
                margin: 0 auto; /* Center the barcode */
                display: block;
              }
              .footer {
                text-align: center;
                margin-top: 5px; /* Reduced margin */
                font-size: 10px; /* Increased font size */
                font-weight: 900; /* Extra bold footer text */
                width: 100%;
                box-sizing: border-box;
              }
              .text-center {
                text-align: center;
              }
              p {
                margin: 1px 0; /* Further reduced margin */
                font-weight: 900; /* Semi-bold for all text */
                width: 100%;
                box-sizing: border-box;
                word-wrap: break-word; /* Allow long words to break */
                white-space: normal; /* Allow text to wrap */
                overflow-wrap: break-word; /* Break words to prevent overflow */
              }
              /* Make observation timestamps more visible */
              .timestamp {
                font-size: 8px;
                font-weight: 700; /* Bold */
                width: 100%;
                box-sizing: border-box;
                display: block;
              }
            </style>
          </head>
          <body>
            <div style="width: 80mm; margin: 0; padding: 0; box-sizing: border-box;">
              <div class="ticket">
              <div class="header">
                <div class="logo-container">
                  <img
                    src="${logoUrl}"
                    alt="${logoConfig.alt || t("app.title")}"
                    onerror="this.style.display='none'; document.getElementById('shop-name').style.fontSize='18px';"
                  />
                </div>
                <div id="shop-name" class="shop-name">${t("app.title")}</div>
                <div class="branch-name">${
                  repair?.branchName
                    ? repair.branchName
                    : t("common.notSpecified")
                }</div>
                <div>${t("repair.repairTicket")} ${formattedTicketNumber}</div>
                <div>${format(new Date(), "dd/MM/yyyy - HH:mm")}</div>
                <div>${format(new Date(), "MMM dd, yyyy - HH:mm")}</div>
                <div style="margin-top: 3px; font-weight: 700;">${t(
                  "repair.shopPhones"
                )}</div>
              </div>

              <div class="section">
                <div class="title">${t("repair.customerInfo")}</div>
                <div class="info-row">
                  <span class="label">${t("repair.customerName")}:</span>
                  <span class="value">${repair.customerName}</span>
                </div>
                <div class="info-row">
                  <span class="label">${t("repair.customerPhone")}:</span>
                  <span class="value">${repair.customerPhone}</span>
                </div>
              </div>

              <div class="section">
                <div class="title">${t("repair.repairDetails")}</div>
                <div class="info-row">
                  <span class="label">${t("repair.phoneModel")}:</span>
                  <span class="value">${repair.phoneModel}</span>
                </div>
                <div class="info-row">
                  <span class="label">${t("repair.stat")}:</span>
                  <span class="value">${t(
                    `repair.status.${repair.status}`
                  )}</span>
                </div>
                <div class="info-row">
                  <span class="label">${t("repair.createdAt")}:</span>
                  <span class="value">${format(
                    new Date(repair.createdAt),
                    "dd/MM/yyyy - HH:mm"
                  )}</span>
                </div>
                <div style="margin-top: 4px; border: 1px solid #000; padding: 2px;">
                  <p style="font-weight: 900; text-align: center; text-decoration: underline; font-size: 10px; margin-bottom: 2px;">${t(
                    "repair.problemDescription"
                  )}:</p>
                  <p style="font-weight: 800; font-size: 9px; text-align: left;">${
                    repair.problemDescription
                  }</p>
                </div>

                ${
                  repair.observations &&
                  Array.isArray(repair.observations) &&
                  repair.observations.length > 0
                    ? `
                <div style="margin-top: 3px; border-top: 1px dashed #000; padding-top: 2px;">
                  <p style="font-weight: 900; text-decoration: underline; font-size: 9px;">${t(
                    "repair.observations"
                  )}:</p>
                  ${repair.observations
                    .map(
                      (obs, index) =>
                        `<div style="margin-top: ${
                          index > 0 ? "3px" : "1px"
                        }; padding-left: 2px;">
                          <p style="font-weight: 900; font-size: 10px;">- ${
                            obs.text
                          }</p>
                          <p style="font-size: 8px; font-weight: 700; text-align: right;">${format(
                            new Date(obs.createdAt),
                            "dd/MM/yyyy - HH:mm"
                          )}</p>
                        </div>`
                    )
                    .join("")}
                </div>
                `
                    : ""
                }
              </div>

              <div class="section">
                <div class="title">${t("repair.paymentStatus")}</div>
                <div class="info-row">
                  <span class="label">${t("repair.initialPrice")}:</span>
                  <span class="value" style="font-weight: 800;">${formatCurrency(
                    repair.repairPrice
                  )}</span>
                </div>

                ${
                  repair.priceModifications &&
                  repair.priceModifications.length > 0
                    ? `<div style="margin-top: 2px; border-top: 1px dashed #000; padding-top: 2px;">
                  <p style="font-weight: 900; text-decoration: underline; font-size: 8px;">${t(
                    "repair.priceModifications"
                  )}:</p>
                  ${repair.priceModifications
                    .map(
                      (mod, index) =>
                        `<div style="margin-top: ${index > 0 ? "2px" : "1px"};">
                          <div>
                            <span style="font-weight: 900; font-size: 10px; display: inline-block; width: 65%;">- ${
                              mod.reason
                            }:</span>
                            <span style="font-weight: 900; font-size: 10px; display: inline-block; width: 35%; text-align: right;">
                              ${mod.amount > 0 ? "+" : ""}${formatCurrency(
                          mod.amount
                        )}
                            </span>
                          </div>
                          <p style="font-size: 8px; font-weight: 700; text-align: right; margin: 0;">
                            ${format(
                              new Date(mod.createdAt),
                              "dd/MM/yyyy - HH:mm"
                            )}
                          </p>
                        </div>`
                    )
                    .join("")}
                  <div style="border-top: 1px dotted #000; padding-top: 2px; margin-top: 2px;">
                    <span style="font-weight: 900; display: inline-block; width: 50%;">${t(
                      "repair.totalPrice"
                    )}:</span>
                    <span style="font-weight: 900; display: inline-block; width: 50%; text-align: right;">${formatCurrency(
                      repair.repairPrice +
                        repair.priceModifications.reduce(
                          (sum, mod) => sum + mod.amount,
                          0
                        )
                    )}</span>
                  </div>
                </div>`
                    : ""
                }

                <div style="margin-top: 3px; border-top: 1px dashed #000; padding-top: 2px;">
                  <div style="margin-bottom: 1px;">
                    <span style="font-weight: 700; display: inline-block; width: 50%;">${t(
                      "repair.downPayment"
                    )}:</span>
                    <span style="font-weight: 700; display: inline-block; width: 50%; text-align: right;">${formatCurrency(
                      repair.downPayment
                    )}</span>
                  </div>
                  <div style="margin-bottom: 1px;">
                    <span style="font-weight: 700; display: inline-block; width: 50%;">${t(
                      "common.balance"
                    )}:</span>
                    <span style="font-weight: 800; display: inline-block; width: 50%; text-align: right;">${formatCurrency(
                      Math.max(
                        0,
                        repair.repairPrice +
                          (repair.priceModifications?.reduce(
                            (sum, mod) => sum + mod.amount,
                            0
                          ) || 0) -
                          repair.downPayment
                      )
                    )}</span>
                  </div>
                  <div>
                    <span style="font-weight: 700; display: inline-block; width: 50%;">${t(
                      "repair.paymentStatus"
                    )}:</span>
                    <span style="font-weight: 800; display: inline-block; width: 50%; text-align: right;">${t(
                      `repair.payment.${repair.paymentStatus}`
                    )}</span>
                  </div>
                </div>
              </div>

              ${
                codeType === "qr"
                  ? `<div class="qr-code">
                      <img src="${printCode}" alt="QR Code" />
                      <p class="text-center" style="font-weight: 900; font-size: 10px;">${t(
                        "repair.scanQrCode"
                      )}: ${repair.id.substring(0, 8)}</p>
                      <p class="text-center" style="font-weight: 900; font-size: 11px;">${t(
                        "repair.ticketNumber"
                      )}: ${formattedTicketNumber}</p>
                    </div>`
                  : `<div class="barcode">
                      <img src="${barcode}" alt="Barcode" />

                      <p class="text-center" style="font-weight: 900; font-size: 11px;">${t(
                        "repair.ticketNumber"
                      )}: ${formattedTicketNumber}</p>
                    </div>`
              }

              <div class="footer">
                <p style="font-weight: 900; font-size: 11px;">${t(
                  "repair.thankYou"
                )}</p>
                <p style="font-weight: 900; font-size: 10px;">${t(
                  "repair.bringReceipt"
                )}</p>
              </div>
            </div>
            </div>
            <script>
              // Function to adjust content for 80mm thermal printer
              function adjustForPrinting() {
                const ticket = document.querySelector('.ticket');
                if (!ticket) return;

                // Set fixed font size for 80mm paper
                document.body.style.fontSize = '10px';
                document.body.style.fontWeight = '900'; // Make all text extra bold

                // Set body width to 80mm
                document.body.style.width = '80mm';
                document.body.style.maxWidth = '80mm';
                document.body.style.margin = '0';
                document.body.style.padding = '0';

                // Set ticket width to 72mm with 4mm margins on each side
                ticket.style.width = '72mm';
                ticket.style.maxWidth = '72mm';
                ticket.style.margin = '0 4mm';
                ticket.style.padding = '0';
                ticket.style.boxSizing = 'border-box';

                // Ensure all content fits within the ticket width and is bold
                const contentElements = ticket.querySelectorAll('*');
                contentElements.forEach(el => {
                  if (el.style) {
                    el.style.maxWidth = '100%';
                    el.style.boxSizing = 'border-box';
                    el.style.wordWrap = 'break-word';
                    el.style.overflowWrap = 'break-word';

                    // Make all text bold
                    if (el.tagName !== 'IMG') { // Skip images
                      el.style.fontWeight = '900';
                    }
                  }
                });

                // Make specific elements even more readable
                const labels = ticket.querySelectorAll('.label');
                labels.forEach(label => {
                  if (label.style) {
                    label.style.fontWeight = '900';
                    label.style.fontSize = '10px';
                  }
                });

                const values = ticket.querySelectorAll('.value');
                values.forEach(value => {
                  if (value.style) {
                    value.style.fontWeight = '900';
                    value.style.fontSize = '10px';
                  }
                });
              }

              // Preload the logo image to ensure it's loaded before printing
              function preloadLogo() {
                return new Promise((resolve) => {
                  const logoImg = document.querySelector('.logo-container img');
                  if (!logoImg) {
                    resolve(); // No logo found, continue
                    return;
                  }

                  if (logoImg.complete) {
                    resolve(); // Image already loaded
                  } else {
                    logoImg.onload = () => resolve(); // Image loaded successfully
                    logoImg.onerror = () => resolve(); // Image failed to load, continue anyway

                    // Set a timeout in case the image takes too long to load
                    setTimeout(resolve, 1000);
                  }
                });
              }

              // Print after ensuring logo is loaded
              document.addEventListener('DOMContentLoaded', async function() {
                adjustForPrinting();
                await preloadLogo();
                setTimeout(function() {
                  window.print();
                  window.onafterprint = function() {
                    window.close();
                  };
                }, 300);
              });

              // Fallback in case DOMContentLoaded doesn't trigger
              window.onload = async function() {
                adjustForPrinting();
                await preloadLogo();
                window.print();
                window.onafterprint = function() {
                  window.close();
                };
              };

              // Adjust on resize (in case the print dialog changes the window size)
              window.addEventListener('resize', adjustForPrinting);
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: "TND",
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!repair) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-3xl mx-auto">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t("common.error")}</AlertTitle>
            <AlertDescription>{t("repair.notFound")}</AlertDescription>
          </Alert>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate("/")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("common.back")} {t("common.dashboard")}
          </Button>
        </div>
      </div>
    );
  }

  const statusColors = {
    pending:
      "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-800",
    inProgress:
      "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-800",
    completed:
      "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-200 dark:border-green-800",
    cancelled:
      "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800",
    returned:
      "bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 border-purple-200 dark:border-purple-800",
  };

  const paymentStatusColors = {
    paid: "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-200 dark:border-green-800",
    partial:
      "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-800",
    unpaid:
      "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800",
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("common.back")} {t("common.dashboard")}
          </Button>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold">
                {t("repair.repairDetails")}
              </h1>
              {repair.ticketNumber && (
                <div className="text-sm font-medium text-gray-500 mt-1">
                  {t("repair.ticketNumber")}: #{repair.ticketNumber}
                </div>
              )}
              <div className="text-sm font-medium text-gray-500 mt-1">
                {t("repairShop.branchName")}:{" "}
                {repair?.branchName
                  ? repair.branchName
                  : t("common.notSpecified")}
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={handlePrintQR}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white border-blue-600 hover:border-blue-700"
              >
                <Printer className="h-4 w-4" />
                {t("common.print")} QR
              </Button>

              <Button
                onClick={handlePrintBarcode}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white border-purple-600 hover:border-purple-700"
              >
                <Printer className="h-4 w-4" />
                {t("common.print")} {t("scanner.barcode")}
              </Button>

              <Button
                onClick={handleDownloadQR}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white border-green-600 hover:border-green-700"
              >
                <Download className="h-4 w-4" />
                {t("common.download")} QR
              </Button>

              <Button
                onClick={handlePrintTicketWithQR}
                className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white border-orange-600 hover:border-orange-700 font-semibold"
              >
                <Ticket className="h-4 w-4" />
                {t("repair.printTicket")}
              </Button>

              <Button
                variant="outline"
                onClick={handleOpenPrintDialog}
                className="flex items-center gap-2 border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <Ticket className="h-4 w-4" />
                {t("repair.selectCodeType")}
              </Button>

              <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>{t("repair.selectCodeType")}</DialogTitle>
                    <DialogDescription>
                      {t("repair.selectCodeTypeDescription")}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex flex-col space-y-4 py-4">
                    <RadioGroup
                      value={codeType}
                      onValueChange={(value) =>
                        setCodeType(value as "qr" | "barcode")
                      }
                      className="flex flex-col space-y-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="qr" id="qr" />
                        <Label htmlFor="qr" className="cursor-pointer">
                          {t("repair.qrCode")}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="barcode" id="barcode" />
                        <Label htmlFor="barcode" className="cursor-pointer">
                          {t("scanner.barcode")}
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setPrintDialogOpen(false)}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      onClick={() => {
                        setPrintDialogOpen(false);
                        handlePrintTicket();
                      }}
                      className="bg-orange-600 hover:bg-orange-700 text-white border-orange-600 hover:border-orange-700"
                    >
                      <Printer className="h-4 w-4 mr-2" />
                      {t("common.print")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Debug buttons removed */}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6">
            <Card className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 pb-2 flex flex-row justify-between items-center">
                <CardTitle className="flex items-center">
                  <User className="h-5 w-5 mr-2 text-blue-500" />
                  {t("repair.customerInfo")}
                </CardTitle>
                <Badge className={statusColors[repair.status]}>
                  {t(`repair.status.${repair.status}`)}
                </Badge>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-4">
                  {/* Customer Name - Larger and more prominent */}
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center mr-3">
                      <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground font-medium">
                        {t("repair.customerName")}
                      </p>
                      <p className="text-lg font-semibold text-foreground">
                        {repair.customerName}
                      </p>
                    </div>
                    <div className="ml-auto">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400"
                        onClick={() => {
                          navigator.clipboard.writeText(repair.customerName);
                          toast.success(t("common.copied"));
                        }}
                        title={t("common.copy")}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Phone Number with Call Button */}
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mr-3">
                      <Phone className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground font-medium">
                        {t("repair.customerPhone")}
                      </p>
                      <p className="text-base font-medium text-foreground">
                        {repair.customerPhone}
                      </p>
                    </div>
                    <div className="ml-auto flex">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-green-600 dark:hover:text-green-400"
                        onClick={() => {
                          navigator.clipboard.writeText(repair.customerPhone);
                          toast.success(t("common.copied"));
                        }}
                        title={t("common.copy")}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-green-600 dark:hover:text-green-400"
                        onClick={() =>
                          window.open(`tel:${repair.customerPhone}`)
                        }
                        title={t("common.call")}
                      >
                        <PhoneCall className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Branch Name */}
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center mr-3">
                      <Store className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground font-medium">
                        {t("repairShop.branchName")}
                      </p>
                      <p className="text-base font-medium text-foreground">
                        {repair?.branchName
                          ? repair.branchName
                          : t("common.notSpecified")}
                      </p>
                    </div>
                    <div className="ml-auto">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            repair?.branchName || ""
                          );
                          toast.success(t("common.copied"));
                        }}
                        title={t("common.copy")}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Device Model */}
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center mr-3">
                      <Smartphone className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground font-medium">
                        {t("repair.phoneModel")}
                      </p>
                      <p className="text-base font-medium text-foreground">
                        {repair.phoneModel}
                      </p>
                    </div>
                    <div className="ml-auto">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-purple-600 dark:hover:text-purple-400"
                        onClick={() => {
                          navigator.clipboard.writeText(repair.phoneModel);
                          toast.success(t("common.copied"));
                        }}
                        title={t("common.copy")}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Ticket Number */}
                  {repair.ticketNumber && (
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center mr-3">
                        <Ticket className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground font-medium">
                          {t("repair.ticketNumber")}
                        </p>
                        <p className="text-base font-medium text-foreground">
                          #{repair.ticketNumber}
                        </p>
                      </div>
                      <div className="ml-auto">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-amber-600 dark:hover:text-amber-400"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              repair.ticketNumber?.toString() || ""
                            );
                            toast.success(t("common.copied"));
                          }}
                          title={t("common.copy")}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Creation Date */}
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center mr-3">
                      <Calendar className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground font-medium">
                        {t("repair.createdAt")}
                      </p>
                      <p className="text-base font-medium text-foreground">
                        {format(
                          new Date(repair.createdAt),
                          "dd/MM/yyyy - HH:mm"
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Completion Date (if available) */}
                  {repair.completedAt && (
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mr-3">
                        <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground font-medium">
                          {t("repair.completedAt")}
                        </p>
                        <p className="text-base font-medium text-foreground">
                          {format(
                            new Date(repair.completedAt),
                            "dd/MM/yyyy - HH:mm"
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950 dark:to-orange-950 pb-2">
                <CardTitle className="flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-red-500" />
                  {t("repair.problemDescription")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="flex items-start p-4 bg-muted rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center mr-3">
                    <FileText className="h-5 w-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-foreground whitespace-pre-wrap">
                      {repair.problemDescription}
                    </p>
                  </div>
                  <div className="ml-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          repair.problemDescription
                        );
                        toast.success(t("common.copied"));
                      }}
                      title={t("common.copy")}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950 dark:to-cyan-950 pb-2">
                <CardTitle className="flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-blue-500" />
                  {t("repair.repairTimeline")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <RepairTimeline repair={repair} />
              </CardContent>
            </Card>

            {/* Observations Card */}
            <Card className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950 dark:to-indigo-950 pb-2">
                <CardTitle className="flex items-center">
                  <ClipboardList className="h-5 w-5 mr-2 text-purple-500" />
                  {t("repair.observations")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {/* Add new observation */}
                <div className="flex gap-2 p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg">
                  <Input
                    placeholder={t("repair.addObservation") + "..."}
                    value={newObservation}
                    onChange={(e) => setNewObservation(e.target.value)}
                    className="flex-1 border-purple-200 dark:border-purple-800 focus-visible:ring-purple-500"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleAddObservation}
                    className="border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900 hover:text-purple-700 dark:hover:text-purple-300"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {/* List of observations */}
                <div className="space-y-3">
                  {repair.observations &&
                  Array.isArray(repair.observations) &&
                  repair.observations.length > 0 ? (
                    repair.observations.map((observation) => (
                      <div
                        key={observation.id}
                        className="flex items-start p-4 bg-muted rounded-lg border border-border hover:border-purple-200 dark:hover:border-purple-800 transition-colors"
                      >
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center mr-3">
                          <ClipboardList className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">
                            {observation.text}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1 flex items-center">
                            <Calendar className="h-3 w-3 mr-1 inline" />
                            {format(
                              new Date(observation.createdAt),
                              "dd/MM/yyyy - HH:mm"
                            )}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 px-4 bg-muted rounded-lg">
                      <ClipboardList className="h-10 w-10 text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground text-center">
                        {t("repair.noObservations")}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 pb-2">
                <CardTitle className="flex items-center">
                  <Receipt className="h-5 w-5 mr-2 text-green-600" />
                  {t("repair.paymentStatus")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-6">
                {/* Initial Price (Read-only) */}
                <div className="flex items-center p-3 bg-muted rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mr-3">
                    <Receipt className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground font-medium">
                      {t("repair.initialPrice")}
                    </p>
                    <p className="text-lg font-semibold text-foreground">
                      {formatCurrency(repair.repairPrice)}
                    </p>
                  </div>
                </div>

                {/* Price Modifications */}
                {repair.priceModifications &&
                  repair.priceModifications.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-medium text-muted-foreground flex items-center">
                        <AlertTriangle className="h-4 w-4 mr-1 text-amber-500" />
                        {t("repair.priceModifications")}
                      </h3>
                      <div className="space-y-2">
                        {repair.priceModifications.map((mod) => (
                          <div
                            key={mod.id}
                            className="flex items-start p-3 bg-muted rounded-lg border border-border"
                          >
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center mr-3">
                              {mod.amount > 0 ? (
                                <ArrowLeft className="h-4 w-4 text-green-600 dark:text-green-400 rotate-[135deg]" />
                              ) : (
                                <ArrowLeft className="h-4 w-4 text-red-600 dark:text-red-400 rotate-[315deg]" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between">
                                <span className="text-sm font-medium text-foreground">
                                  {mod.reason}
                                </span>
                                <span
                                  className={`text-sm font-bold ${
                                    mod.amount > 0
                                      ? "text-green-600 dark:text-green-400"
                                      : "text-red-600 dark:text-red-400"
                                  }`}
                                >
                                  {mod.amount > 0 ? "+" : ""}
                                  {formatCurrency(mod.amount)}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1 flex items-center">
                                <Calendar className="h-3 w-3 mr-1 inline" />
                                {format(
                                  new Date(mod.createdAt),
                                  "dd/MM/yyyy - HH:mm"
                                )}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Add Price Modification */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-muted-foreground flex items-center">
                    <Plus className="h-4 w-4 mr-1 text-blue-500" />
                    {t("repair.addPriceModification")}
                  </h3>
                  <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                    <div className="flex flex-col gap-3">
                      <Input
                        type="number"
                        placeholder={t("repair.amount")}
                        value={
                          newPriceModification.amount === 0
                            ? ""
                            : newPriceModification.amount
                        }
                        onChange={(e) =>
                          setNewPriceModification({
                            ...newPriceModification,
                            amount: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="flex-1 border-blue-200 dark:border-blue-800 focus-visible:ring-blue-500"
                      />
                      <div className="flex gap-2">
                        <Input
                          placeholder={t("repair.reason")}
                          value={newPriceModification.reason}
                          onChange={(e) =>
                            setNewPriceModification({
                              ...newPriceModification,
                              reason: e.target.value,
                            })
                          }
                          className="flex-1 border-blue-200 dark:border-blue-800 focus-visible:ring-blue-500"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={handleAddPriceModification}
                          disabled={
                            !newPriceModification.reason.trim() ||
                            newPriceModification.amount === 0
                          }
                          className="border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900 hover:text-blue-700 dark:hover:text-blue-300"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Summary Section */}
                <div className="p-4 bg-muted rounded-lg space-y-3 border border-border">
                  {/* Total Price (with modifications) */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">
                      {t("repair.totalPrice")}
                    </span>
                    <span className="text-lg font-bold text-foreground">
                      {formatCurrency(
                        repair.repairPrice +
                          (repair.priceModifications?.reduce(
                            (sum, mod) => sum + mod.amount,
                            0
                          ) || 0)
                      )}
                    </span>
                  </div>

                  {/* Down Payment */}
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <span className="text-sm font-medium text-muted-foreground">
                      {t("repair.downPayment")}
                    </span>
                    <span className="text-base font-medium text-foreground">
                      {formatCurrency(repair.downPayment)}
                    </span>
                  </div>

                  {/* Balance */}
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <span className="text-sm font-medium text-muted-foreground">
                      {t("common.balance")}
                    </span>
                    <span className="text-lg font-bold text-foreground">
                      {formatCurrency(
                        Math.max(
                          0,
                          repair.repairPrice +
                            (repair.priceModifications?.reduce(
                              (sum, mod) => sum + mod.amount,
                              0
                            ) || 0) -
                            repair.downPayment
                        )
                      )}
                    </span>
                  </div>

                  {/* Payment Status */}
                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <span className="text-sm font-medium text-muted-foreground">
                      {t("repair.paymentStatus")}
                    </span>
                    <Badge
                      className={`${
                        paymentStatusColors[repair.paymentStatus]
                      } px-3 py-1 text-sm`}
                    >
                      {t(`repair.payment.${repair.paymentStatus}`)}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-950 dark:to-violet-950 pb-2">
                <CardTitle className="flex items-center">
                  <QrCode className="h-5 w-5 mr-2 text-indigo-500" />
                  {t("repair.scanQrCode")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 flex flex-col items-center">
                {qrCode ? (
                  <div className="p-4 bg-background rounded-lg border border-indigo-100 dark:border-indigo-800 shadow-sm">
                    <img
                      src={qrCode}
                      alt="QR Code"
                      className="w-48 h-48 object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-48 h-48 bg-muted flex items-center justify-center rounded-lg border border-border">
                    <div className="animate-pulse flex flex-col items-center">
                      <div className="h-10 w-10 bg-muted-foreground/20 rounded-full mb-2"></div>
                      <div className="h-4 w-24 bg-muted-foreground/20 rounded"></div>
                    </div>
                  </div>
                )}
                <div className="mt-4 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1 border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-950 hover:text-indigo-700 dark:hover:text-indigo-300"
                    onClick={handlePrintQR}
                  >
                    <Printer className="h-4 w-4" />
                    {t("common.print")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1 border-indigo-200 dark:border-indigo-800 hover:bg-indigo-50 dark:hover:bg-indigo-950 hover:text-indigo-700 dark:hover:text-indigo-300"
                    onClick={handleDownloadQR}
                  >
                    <Download className="h-4 w-4" />
                    {t("common.download")}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950 dark:to-yellow-950 pb-2">
                <CardTitle className="flex items-center">
                  <Wrench className="h-5 w-5 mr-2 text-amber-500" />
                  {t("repair.updateStatus")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {/* Repair Status */}
                <div className="p-3 bg-muted rounded-lg border border-border">
                  <div className="text-sm font-medium text-muted-foreground mb-2 flex items-center">
                    <Clock className="h-4 w-4 mr-1 text-amber-500" />
                    {t("repair.stat")}
                  </div>
                  <Select
                    value={repair.status}
                    onValueChange={handleStatusChange}
                  >
                    <SelectTrigger className="border-amber-200 dark:border-amber-800 focus:ring-amber-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">
                        <div className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-yellow-500 mr-2"></div>
                          {t("repair.status.pending")}
                        </div>
                      </SelectItem>
                      <SelectItem value="inProgress">
                        <div className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                          {t("repair.status.inProgress")}
                        </div>
                      </SelectItem>
                      <SelectItem value="completed">
                        <div className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                          {t("repair.status.completed")}
                        </div>
                      </SelectItem>
                      <SelectItem value="returned">
                        <div className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-purple-500 mr-2"></div>
                          {t("repair.status.returned")}
                        </div>
                      </SelectItem>
                      <SelectItem value="cancelled">
                        <div className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-red-500 mr-2"></div>
                          {t("repair.status.cancelled")}
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Payment Status */}
                <div className="p-3 bg-muted rounded-lg border border-border">
                  <div className="text-sm font-medium text-muted-foreground mb-2 flex items-center">
                    <Receipt className="h-4 w-4 mr-1 text-green-500" />
                    {t("repair.paymentStatus")}
                  </div>
                  <Select
                    value={repair.paymentStatus}
                    onValueChange={handlePaymentStatusChange}
                  >
                    <SelectTrigger className="border-green-200 dark:border-green-800 focus:ring-green-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">
                        <div className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                          {t("repair.payment.paid")}
                        </div>
                      </SelectItem>
                      <SelectItem value="partial">
                        <div className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                          {t("repair.payment.partial")}
                        </div>
                      </SelectItem>
                      <SelectItem value="unpaid">
                        <div className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-red-500 mr-2"></div>
                          {t("repair.payment.unpaid")}
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Payment Confirmation Button */}
                {repair.paymentStatus === "paid" &&
                  !repair.paymentConfirmed && (
                    <Button
                      variant="outline"
                      onClick={() => setPaymentConfirmDialogOpen(true)}
                      className="w-full flex items-center justify-center gap-2 border-green-200 dark:border-green-800 hover:bg-green-50 dark:hover:bg-green-950 hover:text-green-700 dark:hover:text-green-300"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      {t("repair.confirmPayment")}
                    </Button>
                  )}

                {/* Payment Confirmation Status */}
                {repair.paymentStatus === "paid" && repair.paymentConfirmed && (
                  <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="flex items-center text-green-700 dark:text-green-300">
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      <span className="text-sm font-medium">
                        {t("repair.paymentConfirmed")}
                      </span>
                    </div>
                    {repair.paymentConfirmedAt && (
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                        {format(
                          new Date(repair.paymentConfirmedAt),
                          "dd/MM/yyyy - HH:mm"
                        )}
                      </p>
                    )}
                  </div>
                )}

                {/* Print Ticket Button */}
                <Button
                  variant="default"
                  onClick={handleOpenPrintDialog}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600"
                >
                  <Printer className="h-4 w-4" />
                  {t("repair.printTicket")}
                </Button>
              </CardContent>
              <CardFooter className="pt-0">
                <Dialog
                  open={deleteDialogOpen}
                  onOpenChange={setDeleteDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full text-red-500 hover:bg-red-50 border-red-200 mt-2"
                    >
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      {t("repair.deleteRepair")}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="flex items-center text-red-500">
                        <AlertTriangle className="h-5 w-5 mr-2" />
                        {t("common.areYouSure")}
                      </DialogTitle>
                      <DialogDescription>
                        {t("repair.deleteConfirmation")}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                      <Label
                        htmlFor="delete-code"
                        className="mb-2 block font-medium"
                      >
                        {t("repair.enterDeleteCode")}:
                      </Label>
                      <Input
                        id="delete-code"
                        type="password"
                        value={deleteCode}
                        onChange={(e) => setDeleteCode(e.target.value)}
                        placeholder="******"
                        className="mb-2 border-red-200 focus-visible:ring-red-500"
                      />
                      {deleteError && (
                        <p className="text-sm text-red-500 mt-1 flex items-center">
                          <X className="h-4 w-4 mr-1" />
                          {deleteError}
                        </p>
                      )}
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setDeleteDialogOpen(false);
                          setDeleteCode(""); // Reset code when canceling
                          setDeleteError(""); // Reset error when canceling
                        }}
                      >
                        {t("common.cancel")}
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleDelete}
                        disabled={!deleteCode} // Disable if no code entered
                        className="bg-red-500 hover:bg-red-600"
                      >
                        {t("common.delete")}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Payment Confirmation Dialog */}
                <Dialog
                  open={paymentConfirmDialogOpen}
                  onOpenChange={setPaymentConfirmDialogOpen}
                >
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="flex items-center text-green-600">
                        <CheckCircle2 className="h-5 w-5 mr-2" />
                        {t("repair.confirmPaymentTitle")}
                      </DialogTitle>
                      <DialogDescription>
                        {t("repair.confirmPaymentDescription")}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                      <Label
                        htmlFor="confirm-code"
                        className="mb-2 block font-medium"
                      >
                        {t("repair.enterConfirmCode")}:
                      </Label>
                      <Input
                        id="confirm-code"
                        type="password"
                        value={paymentConfirmCode}
                        onChange={(e) => setPaymentConfirmCode(e.target.value)}
                        placeholder="******"
                        className="mb-2 border-green-200 focus-visible:ring-green-500"
                      />
                      {paymentConfirmError && (
                        <p className="text-sm text-red-500 mt-1 flex items-center">
                          <X className="h-4 w-4 mr-1" />
                          {paymentConfirmError}
                        </p>
                      )}
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setPaymentConfirmDialogOpen(false);
                          setPaymentConfirmCode(""); // Reset code when canceling
                          setPaymentConfirmError(""); // Reset error when canceling
                        }}
                      >
                        {t("common.cancel")}
                      </Button>
                      <Button
                        variant="default"
                        onClick={handleConfirmPayment}
                        disabled={!paymentConfirmCode} // Disable if no code entered
                        className="bg-green-500 hover:bg-green-600"
                      >
                        {t("repair.confirm")}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepairDetail;
